import { Redirect, Route, Switch } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { CONNECTIONS } from 'config/constants';
import SpateChurnZero from 'components/SpateChurnZero';
import SpateProductFruits from 'components/SpateProductFruits';

import {
    Admin,
    AlertCreate,
    AlertEdit,
    Alerts,
    AuthError,
    Brand,
    CategoryFive,
    CategoryOne,
    CategoryThree,
    CategoryTrends,
    ExploreOverviewV2,
    ExploreRelatedSearches,
    HandleAuth,
    InstagramTrends,
    LoadingScreen,
    Login,
    NotFound,
    PublishedWatchlist,
    RelatedCategoriesSelection,
    ReportBuilder,
    ReportEdit,
    ReportView,
    Reports,
    Settings,
    Spotlight,
    TikTokTrendsStarterPro,
    TopPosts,
    Watchlist,
    Watchlists,
} from 'views';

import { Auth0ProviderWithRedirectCallback, Logout } from 'components/auth';
import { AssistantProvider } from 'components/AiAssistant';
import AuthRoute from './AuthRoute';

const DashboardChooseIndustry = lazy(
    () =>
        import(
            /* webpackChunkName: "dashboard-choose-chunk" */ 'views/DashboardChooseIndustry'
        ),
);

const DashboardChooseIndustryWithSuspense = (props) => (
    <Suspense fallback={<LoadingScreen />}>
        <DashboardChooseIndustry {...props} />
    </Suspense>
);

function Routes() {
    return (
        <Auth0ProviderWithRedirectCallback>
            <AssistantProvider>
                <SpateChurnZero />
                <SpateProductFruits />
                <Switch>
                    <Redirect
                        from="/trends/:categoryId?"
                        to="/explore/trends/:categoryId?"
                    />
                    <AuthRoute
                        path="/explore/tiktok/:type(trends|brands)/:categoryId?"
                        component={TikTokTrendsStarterPro}
                    />
                    <AuthRoute
                        path="/explore/tiktok/posts/brands/:categoryId?"
                        component={TopPosts}
                    />
                    <AuthRoute
                        path="/explore/tiktok/:breakdown(hashtags)/:type(trends|brands)/:categoryId?"
                        component={TikTokTrendsStarterPro}
                    />
                    <AuthRoute
                        exact
                        path="/explore/related-categories-selection"
                        component={RelatedCategoriesSelection}
                    />
                    <AuthRoute
                        path="/explore/related-categories/:dataSource(tiktok|google|instagram)/:type(trends)/:categoryId?"
                        component={ExploreRelatedSearches}
                    />
                    <AuthRoute
                        path="/explore/related-categories/:type(trends)/:categoryId?"
                        component={ExploreRelatedSearches}
                    />
                    <AuthRoute
                        path="/explore/google/:type(trends|brands)/:categoryId?"
                        component={CategoryTrends}
                    />
                    <AuthRoute
                        path="/explore/:type(trends|brands)/:categoryId?"
                        component={ExploreOverviewV2}
                    />
                    <AuthRoute
                        path="/explore/instagram/:breakdown(hashtags)/:type(trends|brands)/:categoryId?"
                        component={InstagramTrends}
                    />
                    <AuthRoute
                        path="/explore/instagram/:type(trends|brands)/:categoryId?"
                        component={InstagramTrends}
                    />
                    <AuthRoute
                        exact
                        path="/industries/:industryName/:dataSource(google|tiktok|instagram)?"
                        component={CategoryOne}
                    />
                    <AuthRoute
                        exact
                        path="/category(Two|Three|Four)/:categoryId/:dataSource(google|tiktok|instagram)?"
                        component={CategoryThree}
                    />
                    <AuthRoute
                        exact
                        path="/categoryFive/:categoryId/:dataSource(google|tiktok|instagram)?"
                        component={CategoryFive}
                    />
                    <AuthRoute
                        exact
                        path="/categoryFive/:parentId/:categoryId/:dataSource(google|tiktok|instagram)?"
                        component={CategoryFive}
                    />
                    <AuthRoute
                        exact
                        path="/brands/:brandId/:dataSource(google|tiktok|instagram)?"
                        component={Brand}
                    />
                    <AuthRoute exact path="/watchlist" component={Watchlists} />
                    {/* Redirect to Google by default until Popularity index implemented */}
                    <AuthRoute
                        exact
                        path="/watchlist/published/:watchlistId"
                        component={PublishedWatchlist}
                    />
                    <AuthRoute
                        exact
                        path="/watchlist/published/:watchlistId/:dataSource(google|tiktok|instagram)?"
                        component={PublishedWatchlist}
                    />
                    <AuthRoute
                        exact
                        path="/watchlist/:watchlistId"
                        component={Watchlist}
                    />
                    <AuthRoute
                        exact
                        path="/watchlist/:watchlistId/:dataSource(google|tiktok|instagram)?"
                        component={Watchlist}
                    />
                    <AuthRoute exact path="/spotlights" component={Spotlight} />
                    <AuthRoute path="/settings" component={Settings} />
                    <AuthRoute exact path="/alerts" component={Alerts} />
                    <AuthRoute
                        exact
                        path="/alerts/new"
                        component={AlertCreate}
                    />
                    <AuthRoute
                        exact
                        path="/alerts/:alert_config_id"
                        component={AlertEdit}
                    />

                    <AuthRoute exact path="/reports" component={Reports} />
                    <AuthRoute
                        exact
                        path="/reports/new"
                        component={ReportBuilder}
                    />
                    <AuthRoute
                        exact
                        path="/reports/:reportId/edit"
                        component={ReportEdit}
                    />
                    <AuthRoute
                        exact
                        path="/reports/:reportId/view"
                        component={ReportView}
                    />

                    <AuthRoute
                        exact
                        path="/industries"
                        component={DashboardChooseIndustryWithSuspense}
                    />
                    <AuthRoute
                        exact
                        path="/"
                        component={DashboardChooseIndustryWithSuspense}
                    />
                    <AuthRoute
                        path="/admin"
                        component={Admin}
                        forceAdmin={true}
                    />

                    <Route
                        exact
                        path={`/login/:connection(${CONNECTIONS.join('|')})?`}
                        component={Login}
                    />
                    <Route exact path="/logout" component={Logout} />
                    <Route exact path="/auth" component={HandleAuth} />
                    <Route exact path="/auth/error" component={AuthError} />
                    <Route
                        exact
                        path="/about"
                        render={() => {
                            window.location.replace(
                                'https://spate.nyc/about-us',
                            );
                        }}
                    />
                    <Route component={NotFound} />
                </Switch>
            </AssistantProvider>
        </Auth0ProviderWithRedirectCallback>
    );
}

export default Routes;
